import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Toast } from '../shared/ui/Toast';
import { uiBus } from '../lib/uiBus';

/**
 * NotificationContext — toast notification state only.
 *
 * Isolated from LoadingContext so that a toast firing does NOT
 * re-render components that only consume the loading flag.
 */

type NotificationType = 'success' | 'error' | 'info';

interface Notification {
  id: string;
  message: string;
  type: NotificationType;
}

interface NotificationContextType {
  notify: (message: string, type?: NotificationType) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const notify = useCallback((message: string, type: NotificationType = 'info') => {
    const id = Math.random().toString(36).substring(2, 9);
    setNotifications((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    }, 3000);
  }, []);

  // Subscribe to event bus (called by API layer without direct context access)
  useEffect(() => {
    const unsubscribe = uiBus.subscribeNotify(notify);
    return unsubscribe;
  }, [notify]);

  return (
    <NotificationContext.Provider value={{ notify }}>
      <div className="fixed top-6 right-6 z-[200] space-y-3 pointer-events-none">
        <AnimatePresence>
          {notifications.map((n) => (
            <Toast
              key={n.id}
              message={n.message}
              type={n.type}
              onClose={() => setNotifications((prev) => prev.filter((item) => item.id !== n.id))}
            />
          ))}
        </AnimatePresence>
      </div>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};
